import React, { FC, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, Checkbox, CircularProgress } from '@mui/material';
import { Controller, get, useFormContext } from 'react-hook-form';
import {
  AutocompleteInput,
  Item,
} from 'src/components/Forms/FormInputs/styled';
import { useDebounceFn } from 'src/hooks/useDebounceFn';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface MultipleAutocompleteProps {
  name: string;
  options: { label: string; key: string }[];
  loading?: boolean;
  withDebounce?: boolean;
}

export const MultipleAutocomplete: FC<MultipleAutocompleteProps> = ({
  name,
  options,
  loading,
  withDebounce,
}) => {
  const [open, setOpen] = React.useState(false);
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const [inputValue, setInputValue] = useState<any>(watch(name));

  const handleInputChange = ({
    val,
    onChange,
  }: {
    val: any[];
    onChange: (...event: any[]) => void;
  }) => {
    onChange(val.map((option) => option?.key || option));
  };

  const debFunction = useDebounceFn(handleInputChange, 600);

  const error = get(errors, name);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <Autocomplete
          multiple
          disableCloseOnSelect
          disableClearable
          disabled={loading}
          loading={loading}
          open={open}
          value={inputValue}
          isOptionEqualToValue={(option, value) => option.key === value}
          onOpen={handleOpen}
          onClose={handleClose}
          onBlur={onBlur}
          onChange={(_, newValue) => {
            setInputValue(newValue.map((option) => option?.key || option));

            withDebounce
              ? debFunction({ val: newValue, onChange })
              : onChange(newValue.map((option) => option?.key || option));
          }}
          options={options}
          renderTags={() => undefined}
          renderOption={(props, option) => (
            <Item {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={inputValue.some((key: string) => key === option.key)}
              />
              {option.label}
            </Item>
          )}
          renderInput={(params) => (
            <AutocompleteInput
              {...params}
              error={!!error?.message}
              open={open}
              placeholder={open ? 'Search...' : `${value?.length} selected`}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
